import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, CardFooter } from '../components/ui/Card';
import { StatChip } from '../components/ui/StatChip';
import { CtaSection } from '../components/sections/CtaSection';
import { BookingEmbed } from '../components/sections/BookingEmbed';
import { CheckIcon, AwardIcon, LightbulbIcon, BriefcaseIcon, UsersIcon, GraduationCapIcon, StarIcon, TrendingUpIcon, BarChartIcon, BuildingIcon, CalendarIcon, ClipboardCheckIcon, DollarSignIcon, HeartIcon, UserIcon, HandshakeIcon, TargetIcon, BookOpenIcon, PresentationIcon, BedIcon, UtensilsIcon, MapPinIcon, MusicIcon, GlassesIcon, ShoppingBagIcon, PartyPopperIcon, CompassIcon, VideoIcon, ZapIcon, TrophyIcon, MessageCircleIcon, ShieldIcon } from 'lucide-react';
export function ElevateImpact() {
  const [activePersona, setActivePersona] = useState('sales');
  const [activeAgendaDay, setActiveAgendaDay] = useState(0);
  const [activeEventCategory, setActiveEventCategory] = useState(0);
  const eventCategories = [{
    label: 'STRATEGIC ALIGNMENT',
    tagline: 'High-Intensity',
    color: 'from-[#0099A1] to-[#006D73]',
    icon: <TargetIcon className="h-5 w-5" data-id="element-1916" />,
    description: 'Get the entire revenue engine moving in the same direction.',
    events: [{
      name: 'Sales Kickoff (SKO)',
      detail: 'New quotas, territories, and product roadmaps — typically Q1'
    }, {
      name: 'Mid-Year Re-alignment',
      detail: 'Q3 strategy pivot when H1 targets need recalibrating'
    }, {
      name: 'GTM Planning Summit',
      detail: 'Sales + Marketing + Product aligned on new launches or markets'
    }, {
      name: 'RevOps Sprint',
      detail: 'Fix CRM, lead routing, and comp models away from daily distractions'
    }]
  }, {
    label: 'SKILL & ENABLEMENT',
    tagline: 'Growth-Focused',
    color: 'from-[#FF6B65] to-[#E05550]',
    icon: <GraduationCapIcon className="h-5 w-5" data-id="element-1917" />,
    description: 'Invest in making your reps measurably more effective.',
    events: [{
      name: 'Sales Bootcamp',
      detail: 'Intensive onboarding to get new hires ramped and hitting phones faster'
    }, {
      name: 'Negotiation Workshops',
      detail: 'High-stakes closing and procurement navigation with pro coaches'
    }, {
      name: 'Modern Prospecting Intensive',
      detail: 'LinkedIn, AI-driven outreach, and social selling methodology'
    }, {
      name: 'Roleplay War Rooms',
      detail: 'High-pressure simulations where reps fail safely before real prospects'
    }]
  }, {
    label: 'REWARD & RECOGNITION',
    tagline: 'High-Emotion',
    color: 'from-[#D4A853] to-[#B8922E]',
    icon: <TrophyIcon className="h-5 w-5" data-id="element-1918" />,
    description: 'Prevent turnover and reward top-tier performance.',
    events: [{
      name: "President's Club",
      detail: 'Elite all-expenses-paid trip for the top 5–10% of annual performers'
    }, {
      name: 'Incentive Trips (Spiffs)',
      detail: 'Short performance bursts — "Top 3 reps this month get a weekend in Cancun"'
    }, {
      name: "Founders' Circle",
      detail: 'Intimate retreats where top performers get 1-on-1 time with the CEO'
    }]
  }, {
    label: 'CUSTOMER-FACING',
    tagline: 'Revenue-Generating',
    color: 'from-[#374047] to-[#2A3035]',
    icon: <MessageCircleIcon className="h-5 w-5" data-id="element-1919" />,
    description: 'Events with people outside the company that drive revenue.',
    events: [{
      name: 'Customer Advisory Board',
      detail: 'Top 10–15 customers shaping your roadmap over luxury hospitality'
    }, {
      name: 'VIP Client Summit',
      detail: 'White-glove networking that drives renewals and expansion'
    }, {
      name: 'Partner / Channel Retreat',
      detail: 'Align resellers on targets and provide hands-on training'
    }, {
      name: 'Strategic Co-Selling Retreat',
      detail: 'Map shared accounts with partner companies for joint pipeline'
    }]
  }, {
    label: 'LEADERSHIP & CULTURE',
    tagline: 'Foundation-Building',
    color: 'from-[#0099A1] to-[#374047]',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-1920" />,
    description: 'Build the management layer that makes everything else work.',
    events: [{
      name: 'Sales Leadership Offsite',
      detail: 'VP/Director-level headcount, budget, and "State of the Union" planning'
    }, {
      name: 'Culture & Trust Building',
      detail: 'Resolve silo issues between Sales, Engineering, and Marketing'
    }, {
      name: 'Post-Merger Integration',
      detail: 'Merge two sales forces — cultures, methodologies, and comp plans'
    }]
  }];
  const personas = [{
    id: 'sales',
    title: 'Revenue Leaders',
    roles: 'CRO / VP Sales / Sales Enablement / RevOps',
    icon: <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1921" />,
    hook: 'Build a revenue-moving SKO in 48 hours—leave with a ready-to-run plan.',
    value: 'Design an unforgettable SKO/offsite and lift win-rates & quota attainment.'
  }, {
    id: 'executives',
    title: 'C-Suite Support',
    roles: 'Chief of Staff / EA to CEO',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1922" />,
    hook: 'Pre-packaged, compliance-ready board/C-suite retreat you can approve in one memo.',
    value: 'Streamline logistics and calendar for board/C-suite offsites with quick approval.'
  }, {
    id: 'strategy',
    title: 'Strategy Leaders',
    roles: 'Strategy / Transformation / PMO',
    icon: <PresentationIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1923" />,
    hook: 'Three Days Away. One Quarter Ahead.',
    value: 'Create focused strategy sprints away from HQ with facilitated outcomes.'
  }, {
    id: 'partners',
    title: 'Channel & Partners',
    roles: 'Channel / Alliances / Partner Marketing',
    icon: <HandshakeIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1924" />,
    hook: 'Curate a partner summit that upgrades MDF outcomes and pipeline influence.',
    value: 'Host impactful partner councils and tier-1 partner summits.'
  }, {
    id: 'customers',
    title: 'Customer Advocates',
    roles: 'Customer Marketing / Community',
    icon: <UsersIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1925" />,
    hook: 'Blueprint a Customer Advisory Board experience that increases expansion NRR.',
    value: "Create elite customer councils and user group leaders' retreats."
  }, {
    id: 'investors',
    title: 'Investment Leaders',
    roles: 'PE/VC Platform & Portfolio Ops',
    icon: <DollarSignIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1926" />,
    hook: 'Turn your annual portco meetup into a growth lab (templates + faculty included).',
    value: 'Organize portfolio CEO summits and operator bootcamps with measurable outcomes.'
  }, {
    id: 'education',
    title: 'Education Directors',
    roles: 'University Exec-Ed Program Directors',
    icon: <BookOpenIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1927" />,
    hook: 'Co-deliver a custom exec-ed in an immersive setting (CPD-friendly agenda).',
    value: 'Find distinctive venues for custom programs with complete experience packages.'
  }, {
    id: 'hr',
    title: 'People & Culture',
    roles: 'HR Benefits / Wellbeing / DEI',
    icon: <HeartIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1928" />,
    hook: 'Wellbeing x Culture immersion with measurable eNPS lift and CPD-submittable agenda.',
    value: 'Address burnout recovery and culture & belonging initiatives.'
  }, {
    id: 'marketing',
    title: 'Brand Experience',
    roles: 'Corporate Marketing / Field Marketing',
    icon: <StarIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1929" />,
    hook: 'Host a lighthouse client experience that produces case-study content in 72 hours.',
    value: 'Create flagship clienteling or product-launch councils with measurable outcomes.'
  }];
  const valuePropositions = {
    sales: {
      title: 'Revenue Leaders',
      subtitle: 'CRO / VP Sales / Sales Enablement / RevOps',
      headline: 'Build a revenue-moving SKO in 48 hours—leave with a ready-to-run plan.',
      supportingLine: 'Prototype your next SKO/offsite (or Partner Summit / CAB) with measurable outcomes: win-rate, pipeline conversion, quota attainment, and manager execution.',
      points: ['Ready-to-run agenda + facilitation plan (SKO / Partner Summit / CAB)', 'Revenue impact model + simple dashboard (pipeline + NRR + execution metrics)', 'Deployable enablement kit (talk tracks, breakout templates, manager cadence, reinforcement plan)', 'Peer working sessions with NA revenue leaders (co-build and pressure-test your plan)'],
      riskRemover: 'Not a sales pitch. Approval-friendly package available (official letter, objectives, value disclosure).',
      image: 'https://cdn.magicpatterns.com/uploads/qPSfGoFgHMyKzrCYtPCFHb/Revenue_Leaders_(1).webp'
    },
    executives: {
      title: 'C-Suite Support',
      subtitle: 'Chiefs of Staff, Executive Assistants, Board Coordinators',
      points: ["'One-memo approval' package with complete agenda, budget, and ROI sheet", 'Risk/compliance documentation ready for legal review', 'Turnkey logistics that reduce your planning overhead by 80%', 'Executive-ready presentations to secure immediate buy-in'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gNWosE5Jbo5bFaRab3kErc/C-Suite_Support.webp'
    },
    strategy: {
      title: 'Strategy Leaders',
      subtitle: 'Strategy, Transformation & PMO Directors',
      points: ['Facilitated strategy sprint framework with proven outcomes', 'Quarterly cadence template to maintain momentum post-event', 'Cross-functional alignment tools designed by top consultants', 'Implementation roadmap with accountable next steps'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ipW6Tej1KGgnHm3RZoXnjq/Strategy_Leaders.webp'
    },
    partners: {
      title: 'Channel & Partners',
      subtitle: 'Alliances, Channel & Partner Marketing Leaders',
      points: ['Partner summit blueprint that maximizes MDF utilization', 'Pipeline influence measurement framework', 'Tier-based partner experience design with scalable components', 'Co-marketing and enablement templates ready for implementation'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xnn63jppx6boMThv6udZ2i/Channel__Partners.webp'
    },
    customers: {
      title: 'Customer Advocates',
      subtitle: 'Customer Marketing, Community & Developer Relations',
      points: ['Customer Advisory Board framework that drives expansion revenue', 'Community leader engagement model with retention metrics', 'Voice of Customer capture methodology with product feedback loop', 'Case study and testimonial generation process built-in'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cVKntsF6xzcD7RLw6fhj5g/customer_advocates.webp'
    },
    investors: {
      title: 'Investment Leaders',
      subtitle: 'PE/VC Platform & Portfolio Operations',
      points: ['Portfolio CEO summit design with value-creation workshops', 'Cross-portfolio playbook exchange framework', 'Filmed case content for LP communications', 'Operator bootcamp template with measurable acceleration metrics'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iuCbZHofR8exRRSctpdxiv/Investment_Leaders.webp'
    },
    education: {
      title: 'Education Directors',
      subtitle: 'University Exec-Ed & Custom Program Directors',
      points: ['White-label executive education environment in an iconic setting', 'CPD-compliant agenda structure ready for accreditation', 'Sponsor-ready program components with monetization options', 'Faculty support and content integration with your curriculum'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tkXanW38ZiLxzQB2DMayJJ/Education_Directors.webp'
    },
    hr: {
      title: 'People & Culture',
      subtitle: 'HR, Wellbeing & DEI Leaders',
      points: ['CPD-submittable learning hours for professional development', 'Measurable wellbeing outcomes with eNPS impact tracking', 'Culture rituals designed for continuation back at the workplace', 'Belonging and inclusion frameworks for diverse teams'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8uiDfdd9ntoVUrd6vwC6mh/people_and_culture.webp'
    },
    marketing: {
      title: 'Brand Experience',
      subtitle: 'Corporate Marketing, Brand & Field Marketing',
      points: ['Lighthouse client experience blueprint with measurable outcomes', 'Case study content production integrated into the experience', 'Product launch council framework with influencer components', 'Experiential marketing measurement with attribution modeling'],
      image: 'https://cdn.magicpatterns.com/uploads/ntcxpQN6EhbZY2q1wrvkeu/Brand_Experience_(1).webp'
    }
  };
  const agendaItems = [{
    day: 'Day 1',
    timeframe: 'Afternoon & Evening',
    items: [{
      time: '2:00 PM',
      title: 'VIP Arrivals & Welcome',
      description: 'Private check-in experience with personalized welcome'
    }, {
      time: '3:30 PM',
      title: 'Destination ROI Masterclass',
      description: '90-minute session on maximizing event impact and measuring outcomes'
    }, {
      time: '6:00 PM',
      title: 'Immersive Welcome Reception',
      description: 'Networking and showcase of destination experiences'
    }, {
      time: '7:30 PM',
      title: 'Curated Dining Experience',
      description: 'Signature culinary journey with facilitated connections'
    }, {
      time: '9:00 PM',
      title: 'Live Entertainment Experience',
      description: '6-star resort live entertainment, featuring world-class performances in a refined luxury setting'
    }]
  }, {
    day: 'Day 2',
    timeframe: 'Full Day Experience',
    items: [{
      time: '7:00 AM',
      title: 'Optional Wellness Activity',
      description: 'Sunrise yoga or guided beach meditation'
    }, {
      time: '8:30 AM',
      title: 'Breakfast & Track Selection',
      description: 'Choose your focused learning track'
    }, {
      time: '9:30 AM',
      title: 'Specialized Tracks',
      description: 'Revenue SKO / People & Culture / Partner Council (3 hours)'
    }, {
      time: '12:30 PM',
      title: 'Collaborative Lunch',
      description: 'Shared insights and cross-track pollination'
    }, {
      time: '2:00 PM',
      title: 'Site Tours & Experiential Showcase',
      description: 'Curated exploration of venue capabilities'
    }, {
      time: '4:00 PM',
      title: 'Design Lab',
      description: 'Facilitated workshop to apply learnings to your specific needs'
    }, {
      time: '7:00 PM',
      title: 'Signature Networking Experience',
      description: 'Immersive evening with local cultural elements'
    }, {
      time: '9:30 PM',
      title: 'Themed Party Night',
      description: '6-star resort themed entertainment experience, showcasing destination-level production and atmosphere'
    }]
  }, {
    day: 'Day 3',
    timeframe: 'Morning',
    items: [{
      time: '8:00 AM',
      title: 'Breakfast & Reflection',
      description: 'Guided discussion on key takeaways'
    }, {
      time: '9:00 AM',
      title: 'Playbook Finalization',
      description: 'Complete your actionable framework with expert guidance'
    }, {
      time: '10:30 AM',
      title: 'One-to-One Consultations',
      description: 'Personalized next steps with our specialists'
    }, {
      time: '12:00 PM',
      title: 'Farewell Lunch & Departures',
      description: 'Closing remarks and continuation planning'
    }]
  }];
  const resortActivities = [{
    title: 'Live Music & Concerts',
    icon: <MusicIcon className="h-8 w-8 text-white" data-id="element-1930" />,
    description: 'Enjoy nightly concerts featuring top tribute bands and local talent on multiple stages throughout the resort.'
  }, {
    title: 'Legendary Pool Parties',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1931" />,
    description: 'Experience high-energy pool parties featuring DJs, foam activations, and curated poolside entertainment.'
  }, {
    title: 'Themed Nights & Shows',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1932" />,
    description: 'Participate in themed dinner shows, Mexican fiestas, and professional entertainment productions.'
  }, {
    title: 'Luxury Spa Treatments',
    icon: <HeartIcon className="h-8 w-8 text-white" data-id="element-1933" />,
    description: 'Unwind with signature wellness treatments, massages, and rejuvenating therapies.'
  }, {
    title: 'Premium Amenities',
    icon: <GlassesIcon className="h-8 w-8 text-white" data-id="element-1934" />,
    description: 'Access exclusive resort amenities, premium facilities, and personalized services.'
  }, {
    title: 'VIP Access Benefits',
    icon: <ShoppingBagIcon className="h-8 w-8 text-white" data-id="element-1935" />,
    description: 'Enjoy exclusive access to premium amenities, priority reservations, and personalized concierge service.'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1936">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden min-h-[600px]" data-id="element-1937">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-1938">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6bivUiY37qndHSDGqhUPNF/Complimentary_Retreat_Hero_Page.webp" alt="Luxury Retreat" className="w-full h-full object-cover scale-105" data-id="element-1939" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" data-id="element-1940"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1941">
          <div className="max-w-3xl" data-id="element-1942">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30" data-id="element-1943">
              <UsersIcon className="h-4 w-4 mr-2" data-id="element-1944" />
              Executive Retreat • Capped at 20 Leaders
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 [text-shadow:_0_2px_20px_rgb(0_0_0_/_80%)]" data-id="element-1945">
              Your Complimentary Luxury Retreat
            </h1>
            <p className="text-xl md:text-2xl text-white mb-8 [text-shadow:_0_2px_16px_rgb(0_0_0_/_70%)] leading-relaxed" data-id="element-1946">
              Elevate Your Revenue Strategy in 48 Hours With Elite Peers
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8" data-id="element-1947">
              <Button variant="primary" size="lg" to="/contact" data-id="element-1948">
                Check Eligibility
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20" data-id="element-1949">
                15-Min Fit Check
              </Button>
            </div>
            <p className="text-sm text-white/80 mt-4 [text-shadow:_0_2px_12px_rgb(0_0_0_/_60%)]" data-id="element-1950">
              Approval-safe • Compliance documentation provided • No sales pitch
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar - Immediate Credibility */}
      <section className="py-8 bg-[#374047]" data-id="element-1951">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1952">
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/90 text-sm" data-id="element-1953">
            <div className="flex items-center gap-2" data-id="element-1954">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1955" />
              <span data-id="element-1956">INSEAD/Wharton/MIT Faculty</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1957">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1958" />
              <span data-id="element-1959">Peer-Level Cohorts Only</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1960">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1961" />
              <span data-id="element-1962">Tangible Work Product</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1963">
              <CheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-1964" />
              <span data-id="element-1965">Compliance-Ready Package</span>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Leave With - Front and Center */}
      <section className="py-16 bg-white" data-id="element-1966">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1967">
          <div className="max-w-4xl mx-auto" data-id="element-1968">
            <div className="text-center mb-12" data-id="element-1969">
              <div className="inline-flex items-center px-4 py-2 bg-[#0099A1]/10 rounded-full text-[#0099A1] text-sm font-medium mb-4" data-id="element-1970">
                <ClipboardCheckIcon className="h-4 w-4 mr-2" data-id="element-1971" />
                Tangible Deliverables
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1972">
                Your SKO Planning Toolkit
              </h2>
              <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-1973">
                Leave with everything you need to plan, budget, and execute a
                world-class SKO
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-id="element-1974">
              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-1975">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-1976">
                  1
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-1977">
                  <CompassIcon className="h-8 w-8 text-white" data-id="element-1978" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-1979">
                  Location Scouting & Budget Framework
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-1980">
                  Curated destination options with venue walkthroughs, capacity
                  planning, and detailed budget estimates for 50-500 attendees
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-1981">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-1982">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-1983" />
                    <span data-id="element-1984">Ready-to-present options</span>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-1985">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-1986">
                  2
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-1987">
                  <MapPinIcon className="h-8 w-8 text-white" data-id="element-1988" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-1989">
                  Experience Design & Journey Map
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-1990">
                  Complete attendee journey from arrival to departure—agenda
                  flow, breakout sessions, networking moments, and signature
                  experiences
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-1991">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-1992">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-1993" />
                    <span data-id="element-1994">End-to-end experience map</span>
                  </div>
                </div>
              </div>

              <div className="group relative bg-gradient-to-br from-[#0099A1]/5 to-[#0099A1]/10 rounded-2xl p-8 border-2 border-[#0099A1]/20 hover:border-[#0099A1]/40 transition-all duration-300" data-id="element-1995">
                <div className="absolute top-6 right-6 w-12 h-12 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] font-bold text-xl" data-id="element-1996">
                  3
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300" data-id="element-1997">
                  <VideoIcon className="h-8 w-8 text-white" data-id="element-1998" />
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-3" data-id="element-1999">
                  Content & Delivery Playbook
                </h3>
                <p className="text-[#374047]/70 leading-relaxed" data-id="element-2000">
                  Session scripts, facilitation guides, presentation templates,
                  and speaker briefs—everything needed to execute flawlessly
                </p>
                <div className="mt-6 pt-6 border-t border-[#0099A1]/20" data-id="element-2001">
                  <div className="flex items-center text-sm text-[#0099A1] font-medium" data-id="element-2002">
                    <CheckIcon className="h-4 w-4 mr-2" data-id="element-2003" />
                    <span data-id="element-2004">Plug-and-play content</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center" data-id="element-2005">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#F4E8D0]/50 rounded-full" data-id="element-2006">
                <TrendingUpIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2007" />
                <span className="text-[#374047] font-medium" data-id="element-2008">
                  Work product valued at $15,000+ in consulting fees
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Persona Selector - Redesigned for better mobile experience */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2009">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2010">
          <div className="max-w-4xl mx-auto text-center mb-12" data-id="element-2011">
            <div className="inline-flex items-center px-4 py-2 bg-white rounded-full text-[#374047] text-sm font-medium mb-4 shadow-sm" data-id="element-2012">
              <UsersIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-2013" />
              Peer-Level Cohorts Only
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2014">
              Who This Is For
            </h2>
            <p className="text-lg text-[#374047]/70 max-w-2xl mx-auto" data-id="element-2015">
              Designed for decision-makers who shape corporate events, offsites,
              and strategic gatherings.
            </p>
          </div>

          {/* Ideal Participant Profile - Redesigned with better visual hierarchy */}
          <div className="max-w-5xl mx-auto mb-12" data-id="element-2016">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[#0099A1]/10" data-id="element-2017">
              <div className="bg-gradient-to-r from-[#0099A1] to-[#006D73] px-8 py-6" data-id="element-2018">
                <h3 className="text-2xl font-bold text-white text-center flex items-center justify-center gap-3" data-id="element-2019">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-2020">
                    <CheckIcon className="h-6 w-6 text-white" data-id="element-2021" />
                  </div>
                  You're a Strong Fit If:
                </h3>
              </div>

              <div className="p-8 md:p-12" data-id="element-2022">
                <div className="grid md:grid-cols-2 gap-8" data-id="element-2023">
                  <div className="relative" data-id="element-2024">
                    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0099A1] to-[#006D73] rounded-full" data-id="element-2025"></div>
                    <div className="space-y-6" data-id="element-2026">
                      <div className="flex items-start gap-4" data-id="element-2027">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-2028">
                          <CalendarIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2029" />
                        </div>
                        <div data-id="element-2030">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-2031">
                            Planning an SKO in 3-12 months
                          </p>
                          <p className="text-[#374047]/60" data-id="element-2032">
                            Want to prototype before committing budget
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-2033">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-2034">
                          <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2035" />
                        </div>
                        <div data-id="element-2036">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-2037">
                            Facing clear growth pressure
                          </p>
                          <p className="text-[#374047]/60" data-id="element-2038">
                            Pipeline conversion, manager execution, or
                            enablement adoption issues
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative" data-id="element-2039">
                    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#0099A1] to-[#006D73] rounded-full" data-id="element-2040"></div>
                    <div className="space-y-6" data-id="element-2041">
                      <div className="flex items-start gap-4" data-id="element-2042">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-2043">
                          <TrendingUpIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2044" />
                        </div>
                        <div data-id="element-2045">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-2046">
                            New in role (first 90-180 days)
                          </p>
                          <p className="text-[#374047]/60" data-id="element-2047">
                            Need to align fast and build credibility
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4" data-id="element-2048">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#0099A1]/10 to-[#0099A1]/20 rounded-xl flex items-center justify-center" data-id="element-2049">
                          <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-2050" />
                        </div>
                        <div data-id="element-2051">
                          <p className="font-bold text-[#374047] text-lg mb-1" data-id="element-2052">
                            Company size: 300-5,000 employees
                          </p>
                          <p className="text-[#374047]/60" data-id="element-2053">
                            North America based, growth-stage or scale-up
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-gray-200" data-id="element-2054">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-2055">
                    <div className="flex items-center gap-3" data-id="element-2056">
                      <div className="w-10 h-10 bg-[#FF6B65]/10 rounded-full flex items-center justify-center" data-id="element-2057">
                        <span className="text-2xl" data-id="element-2058">⚡</span>
                      </div>
                      <p className="text-[#374047] font-medium" data-id="element-2059">
                        <strong data-id="element-2060">Typical profile:</strong> New CRO/VP Sales,
                        RevOps, or Enablement leader
                      </p>
                    </div>
                    <Button variant="primary" to="/contact" data-id="element-2061">
                      Check Your Fit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile-optimized role selector */}
          <div className="overflow-x-auto pb-4 mb-6" data-id="element-2062">
            <div className="flex space-x-2 min-w-max px-1" data-id="element-2063">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'bg-white text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-2064">
                  <span className="p-2 bg-white/80 rounded-full mb-2" data-id="element-2065">
                    {persona.icon}
                  </span>
                  <span className="text-center whitespace-nowrap" data-id="element-2066">
                    {persona.title}
                  </span>
                  {activePersona === persona.id && <div className="h-1 w-12 bg-[#FF6B65] rounded-full mt-2" data-id="element-2067"></div>}
                </button>)}
            </div>
          </div>
          {/* Active persona indicator */}
          <div className="bg-[#F4E8D0]/30 rounded-lg p-4 mb-6" data-id="element-2068">
            <div className="flex items-center justify-between" data-id="element-2069">
              <div className="flex items-center" data-id="element-2070">
                <div className="bg-[#0099A1] p-2 rounded-full text-white mr-3" data-id="element-2071">
                  {personas.find(p => p.id === activePersona)?.icon}
                </div>
                <div data-id="element-2072">
                  <h3 className="font-bold text-[#374047]" data-id="element-2073">
                    {valuePropositions[activePersona].title}
                  </h3>
                  <p className="text-sm text-[#374047]/70" data-id="element-2074">
                    {personas.find(p => p.id === activePersona)?.roles}
                  </p>
                </div>
              </div>
              <div className="bg-[#0099A1]/10 px-3 py-1 rounded-full text-xs text-[#0099A1] font-medium" data-id="element-2075">
                Selected
              </div>
            </div>
          </div>
          {/* Active Persona Value Proposition - Redesigned without image */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-8" data-id="element-2076">
            {/* Hero Image for Active Persona */}
            <div className="relative h-64 md:h-80 overflow-hidden" data-id="element-2077">
              <img src={valuePropositions[activePersona].image} alt={valuePropositions[activePersona].title} className="w-full h-full object-cover object-center" data-id="element-2078" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-2079"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-2080">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg" data-id="element-2081">
                  {valuePropositions[activePersona].title}
                </h3>
                <p className="text-white/95 text-sm md:text-base drop-shadow-lg" data-id="element-2082">
                  {valuePropositions[activePersona].subtitle}
                </p>
              </div>
            </div>

            <div className="p-6" data-id="element-2083">
              {activePersona === 'sales' ? <>
                  {/* Revenue Leaders - Executive Outcome Card */}
                  <div className="mb-6" data-id="element-2084">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2085">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-2086">
                        Cohort capped at 20
                      </span>
                    </div>
                    <h4 className="text-2xl md:text-3xl font-bold text-[#374047] mb-3" data-id="element-2087">
                      Build a revenue-moving SKO in 48 hours. Leave with a
                      ready-to-run plan.
                    </h4>
                    <p className="text-lg text-[#374047]/80 mb-6 leading-relaxed" data-id="element-2088">
                      {valuePropositions[activePersona].supportingLine}
                    </p>
                  </div>

                  <h5 className="font-bold text-[#374047] mb-4 text-lg" data-id="element-2089">
                    Key Outcomes:
                  </h5>
                  <div className="space-y-4 mb-6" data-id="element-2090">
                    {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-4 rounded-lg" data-id="element-2091">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2092" />
                          <span className="ml-3 text-[#374047]" data-id="element-2093">{point}</span>
                        </div>)}
                  </div>

                  <p className="text-sm text-[#374047]/60 mb-6 italic" data-id="element-2094">
                    {valuePropositions[activePersona].riskRemover}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-2095">
                    <Button variant="primary" to="/contact" className="flex-1 sm:flex-initial" data-id="element-2096">
                      Book 15-Min Call
                    </Button>
                    <Button variant="secondary" to="/contact" className="flex-1 sm:flex-initial" data-id="element-2097">
                      Check Eligibility
                    </Button>
                  </div>
                </> : <>
                  {/* Other personas - existing format */}
                  <div className="border-l-4 border-[#FF6B65] pl-4 mb-6" data-id="element-2098">
                    <p className="text-lg italic text-[#374047]" data-id="element-2099">
                      "{personas.find(p => p.id === activePersona)?.hook}"
                    </p>
                  </div>
                  <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2100">
                    {personas.find(p => p.id === activePersona)?.value}
                  </p>
                  <h4 className="font-bold text-[#374047] mb-4" data-id="element-2101">
                    Key Benefits:
                  </h4>
                  <div className="space-y-4 mb-8" data-id="element-2102">
                    {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-3 rounded-lg" data-id="element-2103">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2104" />
                          <span className="ml-3 text-[#374047]" data-id="element-2105">{point}</span>
                        </div>)}
                  </div>
                  <div className="flex justify-center" data-id="element-2106">
                    <Button variant="primary" to="/contact" data-id="element-2107">
                      Request Your Invitation
                    </Button>
                  </div>
                </>}
            </div>
          </div>
          {/* Navigation hints */}
          <div className="text-center text-[#374047]/60 text-sm mb-8" data-id="element-2108">
            <p data-id="element-2109">
              Scroll through the roles above to find the best fit for your
              position
            </p>
          </div>
        </div>
      </section>

      {/* What We Build — Event Types Showcase */}
      <section className="py-16 bg-[#374047]" data-id="element-2110">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2111">
          <div className="max-w-5xl mx-auto" data-id="element-2112">
            <div className="text-center mb-10" data-id="element-2113">
              <span className="text-xs font-bold tracking-[0.2em] text-[#0099A1] uppercase" data-id="element-2114">
                Beyond the Retreat
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-3" data-id="element-2115">
                Every Event Your Revenue Org Needs
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto" data-id="element-2116">
                From SKOs to President's Club, from War Rooms to Customer
                Advisory Boards — we design, produce, and execute them all.
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-2117">
              {eventCategories.map((cat, idx) => <button key={idx} onClick={() => setActiveEventCategory(idx)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold tracking-wider transition-all duration-200 ${activeEventCategory === idx ? 'bg-white text-[#374047] shadow-lg' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`} data-id="element-2118">
                  {cat.icon}
                  {cat.label}
                </button>)}
            </div>

            {/* Active Category Content */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-id="element-2119">
              <div className="flex items-center gap-3 mb-4" data-id="element-2120">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${eventCategories[activeEventCategory].color} flex items-center justify-center text-white`} data-id="element-2121">
                  {eventCategories[activeEventCategory].icon}
                </div>
                <div data-id="element-2122">
                  <div className="flex items-center gap-2" data-id="element-2123">
                    <h3 className="text-xl font-bold text-white" data-id="element-2124">
                      {eventCategories[activeEventCategory].label}
                    </h3>
                    <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-white/10 text-white/60" data-id="element-2125">
                      {eventCategories[activeEventCategory].tagline}
                    </span>
                  </div>
                  <p className="text-white/50 text-sm mt-0.5" data-id="element-2126">
                    {eventCategories[activeEventCategory].description}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3 mt-6" data-id="element-2127">
                {eventCategories[activeEventCategory].events.map((event, idx) => <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors" data-id="element-2128">
                      <h4 className="font-bold text-white text-sm mb-1" data-id="element-2129">
                        {event.name}
                      </h4>
                      <p className="text-white/50 text-xs leading-relaxed" data-id="element-2130">
                        {event.detail}
                      </p>
                    </div>)}
              </div>
            </div>

            <div className="text-center mt-8" data-id="element-2131">
              <p className="text-white/40 text-sm mb-4" data-id="element-2132">
                Don't see your event type? We've probably built it. Let's talk.
              </p>
              <Button variant="primary" to="/contact" data-id="element-2133">
                Tell Us What You Need
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Package Details - Reframed as Retreat Structure */}
      <section className="py-12 bg-white" data-id="element-2134">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2135">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-2136">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2137">
              How the Retreat Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2138">
              48 hours of focused work—venue and logistics handled so you can
              focus on building
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2139">
            <div data-id="element-2140">
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-2141">
                <CheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-2142" />
                What's Provided
              </h3>
              <div className="space-y-3 mb-6" data-id="element-2143">
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2144">
                  <div className="flex items-start gap-3" data-id="element-2145">
                    <GraduationCapIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2146" />
                    <div data-id="element-2147">
                      <p className="font-medium text-[#374047]" data-id="element-2148">
                        Elite Faculty Sessions
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2149">
                        INSEAD, Wharton, MIT professors facilitate your working
                        sessions (typical cost: $15,000-25,000 for Power Week)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2150">
                  <div className="flex items-start gap-3" data-id="element-2151">
                    <UsersIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2152" />
                    <div data-id="element-2153">
                      <p className="font-medium text-[#374047]" data-id="element-2154">
                        Peer Working Groups
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2155">
                        Collaborate with 20 other qualified revenue leaders
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2156">
                  <div className="flex items-start gap-3" data-id="element-2157">
                    <BedIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2158" />
                    <div data-id="element-2159">
                      <p className="font-medium text-[#374047]" data-id="element-2160">
                        Accommodations & Meals
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2161">
                        2 nights at a 5+ star resort, all meals and logistics
                        covered
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2162">
                  <div className="flex items-start gap-3" data-id="element-2163">
                    <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2164" />
                    <div data-id="element-2165">
                      <p className="font-medium text-[#374047]" data-id="element-2166">
                        Frameworks & Templates
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2167">
                        Battle-tested tools you customize for your company
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-2168">
                <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-2169" />
                Your Investment
              </h3>
              <div className="space-y-3" data-id="element-2170">
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2171">
                  <div className="flex items-start gap-3" data-id="element-2172">
                    <CalendarIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2173" />
                    <div data-id="element-2174">
                      <p className="font-medium text-[#374047]" data-id="element-2175">
                        Your flights to the destination
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2176">
                        Book your own travel (typically $300-600 roundtrip)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-2177">
                  <div className="flex items-start gap-3" data-id="element-2178">
                    <DollarSignIcon className="h-5 w-5 text-[#0099A1] mt-0.5 flex-shrink-0" data-id="element-2179" />
                    <div data-id="element-2180">
                      <p className="font-medium text-[#374047]" data-id="element-2181">
                        $250 refundable commitment deposit
                      </p>
                      <p className="text-sm text-[#374047]/70" data-id="element-2182">
                        Returned upon attendance—ensures serious participants
                        only
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6" data-id="element-2183">
                <Button variant="primary" to="/contact" className="w-full" data-id="element-2184">
                  Apply for Next Retreat
                </Button>
              </div>
            </div>
            <div data-id="element-2185">
              <Card className="h-full border-2 border-[#0099A1]/30" data-id="element-2186">
                <CardHeader className="bg-[#0099A1] text-white p-4" data-id="element-2187">
                  <h3 className="text-xl font-bold" data-id="element-2188">Approval-Safe Package</h3>
                  <p className="text-sm text-white/90 mt-2" data-id="element-2189">
                    Everything you need for compliance and internal approval
                  </p>
                </CardHeader>
                <CardBody className="p-8 pt-10" data-id="element-2190">
                  <div className="space-y-6" data-id="element-2191">
                    <div className="flex items-start gap-4" data-id="element-2192">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2193">
                        <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2194" />
                      </div>
                      <div data-id="element-2195">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-2196">
                          Official Invitation Letter
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-2197">
                          Educational objectives, agenda, faculty credentials,
                          and hospitality breakdown for legal/compliance review
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-2198">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2199">
                        <BookOpenIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2200" />
                      </div>
                      <div data-id="element-2201">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-2202">
                          CPD-Compliant Agenda
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-2203">
                          Submit for Continuing Professional Development
                          credit—structured as executive education
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-2204">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2205">
                        <DollarSignIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2206" />
                      </div>
                      <div data-id="element-2207">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-2208">
                          Transparent Value Disclosure
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-2209">
                          Line-item breakdown of provided services for
                          compliance documentation
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-2210">
                      <div className="w-10 h-10 bg-[#0099A1]/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-2211">
                        <UserIcon className="h-5 w-5 text-[#0099A1]" data-id="element-2212" />
                      </div>
                      <div data-id="element-2213">
                        <h4 className="font-semibold text-[#374047] mb-1" data-id="element-2214">
                          No-Sell Policy
                        </h4>
                        <p className="text-sm text-[#374047]/70" data-id="element-2215">
                          Written commitment: this is a working retreat, not a
                          sales pitch. No vendor presentations.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-[#F4E8D0]/30 rounded-lg" data-id="element-2216">
                    <p className="text-sm text-[#374047] font-medium" data-id="element-2217">
                      <strong data-id="element-2218">Why this matters:</strong> Many companies have
                      strict gifting policies. Our compliance package makes
                      approval straightforward.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Sample Agenda - Converted to tabs */}
      <section className="py-16 bg-white" data-id="element-2219">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2220">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-2221">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2222">
              Sample 48-Hour Agenda
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2223">
              A carefully crafted experience with modular tracks to address your
              specific needs
            </p>
          </div>
          {/* Day Tabs */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto" data-id="element-2224">
            {agendaItems.map((day, index) => <button key={index} onClick={() => setActiveAgendaDay(index)} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeAgendaDay === index ? 'border-b-2 border-[#0099A1] text-[#0099A1]' : 'text-[#374047]/70 hover:text-[#374047]'}`} data-id="element-2225">
                <div className="flex items-center" data-id="element-2226">
                  <div className="w-6 h-6 rounded-full bg-[#0099A1]/10 text-[#0099A1] flex items-center justify-center mr-2 text-xs" data-id="element-2227">
                    {index + 1}
                  </div>
                  {day.day} - {day.timeframe}
                </div>
              </button>)}
          </div>
          {/* Active Day Content */}
          <div className="bg-[#F4E8D0]/20 rounded-xl p-6" data-id="element-2228">
            <div className="space-y-6" data-id="element-2229">
              {agendaItems[activeAgendaDay].items.map((item, itemIndex) => <div key={itemIndex} className="flex" data-id="element-2230">
                  <div className="w-24 flex-shrink-0 text-[#374047]/70 font-medium" data-id="element-2231">
                    {item.time}
                  </div>
                  <div data-id="element-2232">
                    <h4 className="font-medium text-[#374047]" data-id="element-2233">{item.title}</h4>
                    <p className="text-sm text-[#374047]/70" data-id="element-2234">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="max-w-3xl mx-auto text-center mt-8" data-id="element-2235">
            <p className="text-[#374047]/70" data-id="element-2236">
              This agenda is customized for each cohort based on the specific
              roles and objectives of participants
            </p>
            <p className="text-[#374047]/70 mt-4" data-id="element-2237">
              To make the most of your trip, we recommend arriving in Cancun on
              an early flight and departing on a late return flight, giving you
              more time to fully enjoy the destination.
            </p>
          </div>
        </div>
      </section>
      {/* Luxury Accommodations */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2238">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2239">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2240">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2241">
              Your Luxury Accommodations
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2242">
              Enjoy a complimentary 2-night stay at iconic 5+ star resorts,
              including Palace Resorts, El Dorado, Meliá, SLS, Nobu, Hard Rock &
              more.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start mb-16" data-id="element-2243">
            <div data-id="element-2244">
              <div className="flex items-center mb-6" data-id="element-2245">
                <BedIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-2246" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-2247">
                  Luxurious Oceanfront Experience
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2248">
                Experience the ultimate comfort in meticulously designed rooms
                featuring ocean views, spa amenities, and premium services.
              </p>
              <div className="space-y-3 mb-8" data-id="element-2249">
                <div className="flex items-start" data-id="element-2250">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2251" />
                  <span className="ml-3 text-[#374047]" data-id="element-2252">
                    Spacious suites with breathtaking Caribbean views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2253">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2254" />
                  <span className="ml-3 text-[#374047]" data-id="element-2255">
                    Premium spa and wellness amenities
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2256">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2257" />
                  <span className="ml-3 text-[#374047]" data-id="element-2258">
                    Private balconies with panoramic views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2259">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2260" />
                  <span className="ml-3 text-[#374047]" data-id="element-2261">
                    Premium amenities and 24-hour room service
                  </span>
                </div>
              </div>
              <div className="flex items-center mb-4" data-id="element-2262">
                <UtensilsIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-2263" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-2264">
                  World-Class Dining
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2265">
                Indulge in exceptional culinary experiences across multiple
                restaurants offering international cuisines.
              </p>
              <Button variant="primary" to="/contact" data-id="element-2266">
                Apply for Your Stay
              </Button>
            </div>
            <div data-id="element-2267">
              <div className="grid grid-cols-2 gap-4" data-id="element-2268">
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-2269">
                  <img src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80" alt="Luxury Resort Suite" className="w-full h-64 object-cover" data-id="element-2270" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-2271">
                  <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80" alt="Luxury Resort Pool" className="w-full h-64 object-cover" data-id="element-2272" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-2273">
                  <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" alt="Fine Dining Experience" className="w-full h-64 object-cover" data-id="element-2274" />
                </div>
                <div className="overflow-hidden rounded-lg shadow-lg" data-id="element-2275">
                  <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80" alt="Luxury Beach Resort" className="w-full h-64 object-cover" data-id="element-2276" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* 6-Star Resort Activities */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2277">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2278">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2279">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2280">
              6-Star Resort Entertainment & Activities
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2281">
              Work hard, play harder with these amazing experiences included in
              your stay
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2282">
            {resortActivities.map((activity, index) => <div key={index} className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-xl p-6 text-white shadow-lg transform transition-transform hover:scale-105" data-id="element-2283">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4" data-id="element-2284">
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-2285">{activity.title}</h3>
                <p className="text-white/80" data-id="element-2286">{activity.description}</p>
              </div>)}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-12" data-id="element-2287">
            <p className="text-[#374047]/70 mb-6" data-id="element-2288">
              All activities and entertainment are included in your
              complimentary stay
            </p>
            <Button variant="primary" to="/contact" data-id="element-2289">
              Check Eligibility
            </Button>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 relative overflow-hidden" style={{
      background: 'linear-gradient(to right, #0099A1, #006D73)'
    }} data-id="element-2290">
        <div className="absolute inset-0 opacity-20" data-id="element-2291">
          <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80" alt="Luxury Resort Background" className="w-full h-full object-cover" data-id="element-2292" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-2293">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2294">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30" data-id="element-2295">
              <UsersIcon className="h-4 w-4 mr-2" data-id="element-2296" />
              Next Cohort: Limited to 20 Qualified Leaders
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2297">
              Ready to Build Your Best SKO?
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-2298">
              Apply now for a 15-minute fit check. We'll review your eligibility
              and answer any compliance questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6" data-id="element-2299">
              <Button variant="primary" size="lg" to="/contact" data-id="element-2300">
                Check Eligibility
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20" data-id="element-2301">
                Book 15-Min Call
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/80" data-id="element-2302">
              <div className="flex items-center gap-2" data-id="element-2303">
                <CheckIcon className="h-4 w-4" data-id="element-2304" />
                <span data-id="element-2305">Approval-safe package provided</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2306">
                <CheckIcon className="h-4 w-4" data-id="element-2307" />
                <span data-id="element-2308">No sales pitch policy</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2309">
                <CheckIcon className="h-4 w-4" data-id="element-2310" />
                <span data-id="element-2311">Peer-level cohorts only</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Google Booking Embed */}
      <BookingEmbed data-id="element-2312" />
      <CtaSection data-id="element-2313" />
    </div>;
}